import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import { PATIENT_TRACKING_HUB_PATH } from '../patientTrackingTypes';
import type { PatientTrackingDiscussionChangedNotice } from './patientTrackingDiscussionTypes';

export interface PatientTrackingDiscussionClientOptions {
  targetType: string;
  targetId: string;
  onDiscussionChanged: (notice: PatientTrackingDiscussionChangedNotice) => void;
  onConnectedChange?: (connected: boolean) => void;
}

export interface PatientTrackingDiscussionClient {
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

export function createPatientTrackingDiscussionClient(
  options: PatientTrackingDiscussionClientOptions
): PatientTrackingDiscussionClient {
  const { targetType, targetId, onDiscussionChanged, onConnectedChange } = options;

  let connection: HubConnection | null = null;
  let disposed = false;

  const join = async (conn: HubConnection) => {
    try {
      await conn.invoke('JoinDiscussionAsync', targetType, targetId);
    } catch {
      /* polling fallback */
    }
  };

  const start = async () => {
    if (connection || disposed) return;

    const conn = new HubConnectionBuilder()
      .withUrl(PATIENT_TRACKING_HUB_PATH, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    conn.on('DiscussionChanged', (notice: PatientTrackingDiscussionChangedNotice) => {
      if (notice) onDiscussionChanged(notice);
    });

    conn.onreconnected(() => {
      onConnectedChange?.(true);
      void join(conn);
    });
    conn.onreconnecting(() => onConnectedChange?.(false));
    conn.onclose(() => onConnectedChange?.(false));

    connection = conn;
    try {
      await conn.start();
      if (disposed) {
        await conn.stop();
        return;
      }
      onConnectedChange?.(true);
      await join(conn);
    } catch {
      onConnectedChange?.(false);
    }
  };

  const stop = async () => {
    disposed = true;
    const conn = connection;
    connection = null;
    if (!conn) return;
    try {
      if (conn.state === HubConnectionState.Connected) {
        await conn.invoke('LeaveDiscussionAsync').catch(() => undefined);
      }
    } finally {
      await conn.stop().catch(() => undefined);
    }
  };

  return { start, stop };
}
